// src/components/agent/PublicTeddyChat.tsx
//
// Anonymous public Teddy (M4 dual-mode). Simple request/reply chat —
// NO ChatKit protocol, NO threads, NO streaming: the surface is one
// JSON endpoint (/api/public/teddy) guarded server-side by the admin
// switch, a two-question cumulative guest quota and a daily global
// cap. When the quota is exhausted the server answers 429
// LOGIN_REQUIRED and we politely ask for authentication with a
// sign-in CTA. Unavailable (404) means the admin switched it off.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LogIn, Send } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useTranslation } from "react-i18next";

interface Msg {
  role: "user" | "teddy";
  text: string;
}

const GREETING =
  "Hi, I'm Teddy 🐶 Ask me anything about the BENCHMARK catalog — " +
  "products, brands, prices. You have 2 free questions; sign in " +
  "for unlimited help inside the portal.";

export function PublicTeddyChat({
  onActivity,
}: {
  onActivity?: (s: "idle" | "thinking" | "speaking") => void;
}) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Msg[]>([
    { role: "teddy", text: GREETING },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [loginRequired, setLoginRequired] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, busy]);

  async function send() {
    const text = input.trim();
    if (!text || busy || loginRequired || unavailable) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    onActivity?.("thinking");
    try {
      const r = await fetch("/api/public/teddy", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (r.status === 404) {
        setUnavailable(true);
        setMessages((m) => [
          ...m,
          { role: "teddy", text: "I'm sorry, I'm offline right now. Please try again later or sign in to reach me in the portal." },
        ]);
        return;
      }
      if (r.status === 429) {
        const body = (await r.json().catch(() => ({}))) as {
          detail?: { code?: string; message?: string };
        };
        const msg =
          body.detail?.message ??
          "Please sign in with your BENCHMARK account to keep chatting.";
        setLoginRequired(true);
        setMessages((m) => [...m, { role: "teddy", text: msg }]);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = (await r.json()) as { reply: string; remaining: number };
      setRemaining(body.remaining);
      onActivity?.("speaking");
      setMessages((m) => [...m, { role: "teddy", text: body.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "teddy", text: "I'm sorry, something went wrong on my side. Please try again in a moment." },
      ]);
    } finally {
      setBusy(false);
      window.setTimeout(() => onActivity?.("idle"), 900);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={
              msg.role === "user"
                ? "ml-8 rounded-lg rounded-br-none bg-accent-indigo/20 px-3 py-2 text-sm text-fg-primary"
                : "mr-8 rounded-lg rounded-bl-none bg-bg-elevated px-3 py-2 text-sm text-fg-primary"
            }
          >
            {msg.text}
          </div>
        ))}
        {busy && (
          <div className="mr-8 rounded-lg rounded-bl-none bg-bg-elevated px-3 py-2 text-sm text-fg-tertiary">
            Teddy is thinking…
          </div>
        )}
        {loginRequired && (
          <div className="pt-1">
            <Link to="/login">
              <Button size="sm">
                <LogIn className="h-3.5 w-3.5" strokeWidth={1.5} />
                Sign in to continue
              </Button>
            </Link>
          </div>
        )}
      </div>
      <div className="border-t border-border p-2">
        {remaining !== null && !loginRequired && !unavailable && (
          <div className="px-1 pb-1 text-[10px] uppercase tracking-wider text-fg-quaternary">
            {remaining} free question{remaining === 1 ? "" : "s"} left
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            disabled={busy || loginRequired || unavailable}
            placeholder={
              loginRequired
                ? "Sign in to keep chatting"
                : unavailable
                  ? "Teddy is offline"
                  : "Ask about the catalog…"
            }
            aria-label={t("agent.public.askPlaceholder")}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void send();
            }}
            className="min-w-0 flex-1 rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary placeholder:text-fg-quaternary focus:outline-none focus:ring-2 focus:ring-benchmark-teal disabled:opacity-50"
          />
          <Button
            size="sm"
            onClick={() => void send()}
            disabled={busy || !input.trim() || loginRequired || unavailable}
            aria-label={t("agent.public.sendQuestion")}
          >
            <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    </div>
  );
}
